import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_URL } from '../../api/config';
import { Search, Plus, Edit2, Trash2, X } from 'lucide-react';

const Product = () => {
  const { userToken: token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters / search
  const [searchQuery, setSearchQuery] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Form modal states
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // Image
  const [selectedImage, setSelectedImage] = useState(null);

  // Product form states
  const initialFormData = {
    itemName: '',
    category: '',
    pricingType: 'basePrice',
    basePrice: '',
    mrp: '',
    sellingPrice: '',
    type: '',
    unitType: '',
    variants: []
  };
  const [formData, setFormData] = useState(initialFormData);

  // Options
  const typeOptions = [
    'Beverage', 'Starter', 'Dessert', 'Breads',
    'Main-Course', 'Combo', 'Sweets', 'Snacks', 'Custom'
  ];
  const unitTypes = {
    Size: ['Small', 'Medium', 'Large'],
    Quantity: ['Quarter', 'Half', 'Full'],
    Weight: ['250g', '500g', '1kg'],
    Custom: ['Custom']
  };

  const handleError = useCallback((error, action) => {
    console.error(`Error ${action}:`, error);
    toast.error(`Failed to ${action.toLowerCase()}. Please try again.`);
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      if (!token) {
        throw new Error('Authentication token is missing');
      }
      const response = await fetch(`${API_URL}/products`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message || `Server responded with status: ${response.status}`
        );
      }
      const responseData = await response.json();
      
      // Check for success and data array
      if (!responseData.success || !Array.isArray(responseData.data)) {
        throw new Error('Invalid data format received from server');
      }
      
      setProducts(responseData.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(error?.message || 'Failed to fetch products. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchProducts();
    }
  }, [token, fetchProducts]);

  // Handle file selection for image upload
  const handleImagePick = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage({
        file,
        uri: URL.createObjectURL(file)
      });
    }
  };

  const handleFormSubmit = async (formValues) => {
    try {
      if (!token) throw new Error('Authentication token is missing');

      const formDataToSend = new FormData();
      
      // Append all form fields
      Object.keys(formValues).forEach((key) => {
        if (key === 'variants') {
          formDataToSend.append(key, JSON.stringify(formValues[key]));
        } else if (formValues[key] !== null && formValues[key] !== undefined && formValues[key] !== '') {
          formDataToSend.append(key, formValues[key]);
        }
      });

      // Only append new image if file exists
      if (selectedImage && selectedImage.file) {
        formDataToSend.append('image', selectedImage.file);
      }

      const url = editingProduct
        ? `${API_URL}/products/${editingProduct.id}`
        : `${API_URL}/products`;

      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to save product');
      }

      const result = await response.json();
      console.log('Product saved:', result);

      await fetchProducts();
      resetForm();
      toast.success(`Product ${editingProduct ? 'updated' : 'created'} successfully`);
    } catch (error) {
      console.error('Save product error:', error);
      handleError(error, 'save product');
    }
  };

  const handleDelete = useCallback(
    async (productId) => {
      if (window.confirm('Are you sure you want to delete this product?')) {
        try {
          if (!token) throw new Error('Authentication token is missing');
          const response = await fetch(`${API_URL}/products/${productId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
              'Content-Type': 'application/json'
            }
          });
          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to delete product');
          }
          await fetchProducts();
          toast.success('Product deleted successfully');
        } catch (error) {
          handleError(error, 'delete product');
        }
      }
    },
    [token, fetchProducts, handleError]
  );

  const handleStockUpdate = useCallback(
    async (productId, inStock, duration = null) => {
      try {
        if (!token) throw new Error('Authentication token is missing');
        const response = await fetch(`${API_URL}/products/${productId}/stock`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ inStock, duration })
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.message || 'Failed to update stock status');
        }
        await fetchProducts();
        toast.success('Stock status updated successfully');
      } catch (error) {
        handleError(error, 'update stock status');
      }
    },
    [token, fetchProducts, handleError]
  );

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setSelectedImage(null);
    setEditingProduct(null);
    setIsFormVisible(false);
  }, []);

  const handleEdit = useCallback((product) => {
    console.log('Editing product:', product);
    setEditingProduct(product);
    setFormData({
      itemName: product.itemName || product.item_name || '',
      category: product.category || '',
      pricingType: product.pricingType || product.pricing_type || 'basePrice',
      basePrice: (product.basePrice || product.base_price)?.toString() || '',
      mrp: (product.mrp)?.toString() || '',
      sellingPrice: (product.sellingPrice || product.selling_price)?.toString() || '',
      type: product.type || '',
      unitType: product.unitType || product.unit_type || '',
      variants: product.variants?.map((variant) => ({
        name: variant.name || '',
        price: variant.price?.toString() || ''
      })) || []
    });
    const imageUrl = product.imageUrl || product.image_url;
    setSelectedImage(imageUrl ? { uri: imageUrl } : null);
    setIsFormVisible(true);
  }, []);

  // --- Product Form Component ---
  const ProductForm = () => {
    const [currentFormData, setCurrentFormData] = useState({
      ...formData,
      pricingType: formData.pricingType || 'basePrice',
      mrp: formData.mrp || '',
      sellingPrice: formData.sellingPrice || '',
      variants: formData.variants?.length
        ? formData.variants
        : [{ name: '', price: '' }]
    });
    const [errors, setErrors] = useState({});

    const validateForm = () => {
      const newErrors = {};
      if (!currentFormData.itemName?.trim()) {
        newErrors.itemName = 'Item name is required';
      }
      if (!currentFormData.category) {
        newErrors.category = 'Category is required';
      }
      if (!currentFormData.type) {
        newErrors.type = 'Type is required';
      }
      if (!currentFormData.unitType) {
        newErrors.unitType = 'Unit type is required';
      }

      if (currentFormData.pricingType === 'basePrice') {
        if (!currentFormData.basePrice) {
          newErrors.basePrice = 'Base price is required';
        } else if (
          isNaN(currentFormData.basePrice) ||
          currentFormData.basePrice <= 0
        ) {
          newErrors.basePrice = 'Base price must be a valid positive number';
        }
      } else if (currentFormData.pricingType === 'mrp') {
        if (!currentFormData.mrp) {
          newErrors.mrp = 'MRP is required';
        } else if (isNaN(currentFormData.mrp) || currentFormData.mrp <= 0) {
          newErrors.mrp = 'MRP must be a valid positive number';
        }
        if (!currentFormData.sellingPrice) {
          newErrors.sellingPrice = 'Selling price is required';
        } else if (
          isNaN(currentFormData.sellingPrice) ||
          currentFormData.sellingPrice <= 0
        ) {
          newErrors.sellingPrice = 'Selling price must be a valid positive number';
        } else if (
          parseFloat(currentFormData.sellingPrice) >
          parseFloat(currentFormData.mrp)
        ) {
          newErrors.sellingPrice = 'Selling price cannot be greater than MRP';
        }
      }

      if (currentFormData.variants?.length) {
        const variantErrors = [];
        currentFormData.variants.forEach((variant, index) => {
          const error = {};
          if (!variant.name?.trim()) {
            error.name = 'Variant name is required';
          }
          if (!variant.price) {
            error.price = 'Price is required';
          } else if (isNaN(variant.price) || variant.price <= 0) {
            error.price = 'Price must be a valid positive number';
          }
          if (Object.keys(error).length > 0) {
            variantErrors[index] = error;
          }
        });
        if (variantErrors.length) {
          newErrors.variants = variantErrors;
        }
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const updateBasePriceFromVariants = (variants) => {
      if (variants && variants.length > 0) {
        const prices = variants
          .map((v) => parseFloat(v.price))
          .filter((p) => !isNaN(p));
        if (prices.length > 0) {
          const minPrice = Math.min(...prices);
          setCurrentFormData((prev) => ({
            ...prev,
            basePrice: minPrice.toString()
          }));
        }
      }
    };

    const handleSubmit = () => {
      if (validateForm()) {
        const formattedData = {
          itemName: currentFormData.itemName.trim(),
          category: currentFormData.category,
          type: currentFormData.type,
          unitType: currentFormData.unitType,
          pricingType: currentFormData.pricingType,
          variants: currentFormData.variants.map((variant) => ({
            name: variant.name,
            price: parseFloat(variant.price)
          }))
        };

        if (currentFormData.pricingType === 'basePrice') {
          formattedData.basePrice = parseFloat(currentFormData.basePrice);
        } else if (currentFormData.pricingType === 'mrp') {
          formattedData.mrp = parseFloat(currentFormData.mrp);
          formattedData.sellingPrice = parseFloat(currentFormData.sellingPrice);
        }

        console.log('Submitting form data:', formattedData);
        handleFormSubmit(formattedData);
      }
    };

    const handleVariantChange = (index, field, value) => {
      const updatedVariants = [...currentFormData.variants];
      updatedVariants[index] = { ...updatedVariants[index], [field]: value };
      setCurrentFormData((prev) => ({ ...prev, variants: updatedVariants }));
      if (field === 'price') {
        updateBasePriceFromVariants(updatedVariants);
      }
    };

    const handleUnitTypeSelection = (selectedType) => {
      setCurrentFormData((prev) => ({
        ...prev,
        unitType: selectedType,
        variants: unitTypes[selectedType].map((variant) => ({
          name: variant,
          price: ''
        }))
      }));
    };

    const addVariant = () => {
      setCurrentFormData((prev) => ({
        ...prev,
        variants: [...prev.variants, { name: '', price: '' }]
      }));
    };

    const removeVariant = (index) => {
      const updatedVariants = currentFormData.variants.filter((_, i) => i !== index);
      setCurrentFormData((prev) => ({ ...prev, variants: updatedVariants }));
      updateBasePriceFromVariants(updatedVariants);
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h2 className="text-2xl font-bold text-slate-900">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
            <button 
              onClick={resetForm}
              className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
            >
              <X size={24} className="text-slate-600" />
            </button>
          </div>

          <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6 space-y-6">
            {/* Image Upload */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Product Image</label>
              {selectedImage ? (
                <div className="relative w-full h-48 rounded-xl overflow-hidden border-2 border-slate-200">
                  <img src={selectedImage.uri} alt="Preview" className="w-full h-full object-cover" />
                  <button 
                    onClick={() => setSelectedImage(null)}
                    className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-lg"
                  >
                    <X size={20} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-blue-500 transition-colors bg-slate-50">
                  <Plus size={40} className="text-slate-400 mb-2" />
                  <span className="text-sm font-medium text-slate-500">Click to upload image</span>
                  <input type="file" accept="image/*" onChange={handleImagePick} className="hidden" />
                </label>
              )}
            </div>

            {/* Item Name */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Item Name *</label>
              <input
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                  errors.itemName ? 'border-red-300' : 'border-slate-200'
                }`}
                value={currentFormData.itemName}
                onChange={(e) =>
                  setCurrentFormData((prev) => ({ ...prev, itemName: e.target.value }))
                }
                placeholder="Enter item name"
              />
              {errors.itemName && <p className="text-sm text-red-600">{errors.itemName}</p>}
            </div>

            {/* Category Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Category *</label>
              <div className="flex gap-3">
                {['Veg', 'Non-Veg'].map((cat) => (
                  <button
                    key={cat}
                    className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all border-2 ${
                      currentFormData.category === cat
                        ? cat === 'Veg'
                          ? 'bg-green-500 text-white border-green-500'
                          : 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() =>
                      setCurrentFormData((prev) => ({ ...prev, category: cat }))
                    }
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {errors.category && <p className="text-sm text-red-600">{errors.category}</p>}
            </div>

            {/* Pricing Type */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Pricing Type *</label>
              <div className="flex gap-3">
                <button
                  className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all border-2 ${
                    currentFormData.pricingType === 'basePrice'
                      ? 'bg-blue-700 text-white border-blue-700'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() =>
                    setCurrentFormData((prev) => ({ ...prev, pricingType: 'basePrice' }))
                  }
                >
                  Base Price
                </button>
                <button
                  className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all border-2 ${
                    currentFormData.pricingType === 'mrp'
                      ? 'bg-blue-700 text-white border-blue-700'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() =>
                    setCurrentFormData((prev) => ({ ...prev, pricingType: 'mrp' }))
                  }
                >
                  MRP Based
                </button>
              </div>
            </div>

            {/* Pricing Fields */}
            {currentFormData.pricingType === 'basePrice' ? (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">Base Price *</label>
                <input
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                    errors.basePrice ? 'border-red-300' : 'border-slate-200'
                  }`}
                  value={currentFormData.basePrice}
                  onChange={(e) =>
                    setCurrentFormData((prev) => ({ ...prev, basePrice: e.target.value }))
                  }
                  placeholder="Enter base price"
                  type="number"
                />
                {errors.basePrice && <p className="text-sm text-red-600">{errors.basePrice}</p>}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">MRP *</label>
                  <input
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                      errors.mrp ? 'border-red-300' : 'border-slate-200'
                    }`}
                    value={currentFormData.mrp}
                    onChange={(e) =>
                      setCurrentFormData((prev) => ({ ...prev, mrp: e.target.value }))
                    }
                    placeholder="Enter MRP"
                    type="number"
                  />
                  {errors.mrp && <p className="text-sm text-red-600">{errors.mrp}</p>}
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-slate-700">Selling Price *</label>
                  <input
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                      errors.sellingPrice ? 'border-red-300' : 'border-slate-200'
                    }`}
                    value={currentFormData.sellingPrice}
                    onChange={(e) =>
                      setCurrentFormData((prev) => ({
                        ...prev,
                        sellingPrice: e.target.value
                      }))
                    }
                    placeholder="Enter selling price"
                    type="number"
                  />
                  {errors.sellingPrice && (
                    <p className="text-sm text-red-600">{errors.sellingPrice}</p>
                  )}
                </div>
              </div>
            )}

            {/* Type Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Type *</label>
              <div className="flex flex-wrap gap-2">
                {typeOptions.map((type) => (
                  <button
                    key={type}
                    className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all border-2 ${
                      currentFormData.type === type
                        ? 'bg-blue-700 text-white border-blue-700'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() =>
                      setCurrentFormData((prev) => ({ ...prev, type }))
                    }
                  >
                    {type}
                  </button>
                ))}
              </div>
              {errors.type && <p className="text-sm text-red-600">{errors.type}</p>}
            </div>

            {/* Unit Type Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">Unit Type *</label>
              <div className="flex gap-3">
                {Object.keys(unitTypes).map((type) => (
                  <button
                    key={type}
                    className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all border-2 ${
                      currentFormData.unitType === type
                        ? 'bg-blue-700 text-white border-blue-700'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleUnitTypeSelection(type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
              {errors.unitType && <p className="text-sm text-red-600">{errors.unitType}</p>}
            </div>

            {/* Variants Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-semibold text-slate-700">Variants</label>
                <button 
                  onClick={addVariant}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm hover:bg-blue-200 transition-colors"
                >
                  <Plus size={16} />
                  Add Variant
                </button>
              </div>
              <div className="space-y-3">
                {currentFormData.variants.map((variant, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <input
                      className={`flex-1 px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                        errors.variants?.[index]?.name ? 'border-red-300' : 'border-slate-200'
                      }`}
                      value={variant.name}
                      onChange={(e) =>
                        handleVariantChange(index, 'name', e.target.value)
                      }
                      placeholder="Variant name"
                    />
                    <input
                      className={`w-32 px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                        errors.variants?.[index]?.price ? 'border-red-300' : 'border-slate-200'
                      }`}
                      value={variant.price}
                      onChange={(e) =>
                        handleVariantChange(index, 'price', e.target.value)
                      }
                      placeholder="Price"
                      type="number"
                    />
                    <button
                      onClick={() => removeVariant(index)}
                      className="p-3 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <button 
                onClick={resetForm}
                className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmit}
                className="flex-1 px-6 py-3 bg-blue-700 text-white rounded-xl font-semibold hover:bg-blue-800 transition-colors shadow-lg shadow-blue-100"
              >
                {editingProduct ? 'Update Product' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // --- Product Card Component ---
  const ProductCard = React.memo(({ product }) => {
    const imageUrl = product.imageUrl || product.image_url;
    const itemName = product.itemName || product.item_name;
    const pricingType = product.pricingType || product.pricing_type;
    const basePrice = product.basePrice || product.base_price;
    const sellingPrice = product.sellingPrice || product.selling_price;
    const inStock = product.inStock !== undefined ? product.inStock : product.in_stock;

    return (
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 group">
        <div className="relative overflow-hidden bg-slate-50">
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={itemName} 
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-48 flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
              <span className="text-slate-400 text-sm font-medium">No Image</span>
            </div>
          )}
          
          <div className={`absolute top-3 left-3 px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 shadow-md ${
            product.category === 'Veg' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              product.category === 'Veg' ? 'bg-white' : 'bg-white'
            }`}></div>
            {product.category}
          </div>

          <div className={`absolute top-3 right-3 px-3 py-1.5 rounded-lg font-bold text-xs shadow-md ${
            inStock 
              ? 'bg-emerald-500 text-white' 
              : 'bg-slate-800 text-white'
          }`}>
            {inStock ? 'In Stock' : 'Out of Stock'}
          </div>
        </div>

        <div className="p-5">
          <div className="mb-3">
            <h3 className="font-bold text-slate-900 text-lg mb-1 line-clamp-1">{itemName}</h3>
<p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{product.type}</p>
</div>
      <div className="mb-4">
        {pricingType === 'basePrice' ? (
          <p className="text-2xl font-bold text-slate-900">₹{basePrice}</p>
        ) : (
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold text-slate-900">₹{sellingPrice}</p>
            {product.mrp && (
              <p className="text-sm text-slate-400 line-through">₹{product.mrp}</p>
            )}
          </div>
        )}
      </div>

      {product.variants && product.variants.length > 0 && (
        <div className="mb-4 pb-4 border-b border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Available Sizes</p>
          <div className="flex flex-wrap gap-2">
            {product.variants.slice(0, 4).map((variant, idx) => (
              <span 
                key={idx}
                className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-semibold"
              >
                {variant.name}
              </span>
            ))}
            {product.variants.length > 4 && (
              <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-semibold">
                +{product.variants.length - 4}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => handleEdit(product)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-semibold text-sm hover:bg-slate-200 transition-all"
        >
          <Edit2 size={16} />
          Edit
        </button>
        <button
          onClick={() => handleStockUpdate(product.id, !inStock)}
          className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all ${
            inStock
              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          {inStock ? 'In Stock' : 'Restock'}
        </button>
        <button
          onClick={() => handleDelete(product.id)}
          className="px-4 py-2.5 bg-red-50 text-red-600 rounded-xl font-semibold text-sm hover:bg-red-100 transition-all"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  </div>
);
});
// --- Filtered and Grouped Products ---
const filteredProducts = useMemo(() => {
return products
.filter((product) => {
const itemName = product.itemName || product.item_name || '';
const inStock = product.inStock !== undefined ? product.inStock : product.in_stock;
    const matchesSearch =
      searchQuery === '' ||
      itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.type.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStock =
      stockFilter === 'all' ||
      (stockFilter === 'inStock' && inStock) ||
      (stockFilter === 'outOfStock' && !inStock);
    const matchesType =
      typeFilter === 'all' || product.type === typeFilter;
    return matchesSearch && matchesStock && matchesType;
  })
  .reduce((acc, product) => {
    if (!acc[product.type]) acc[product.type] = [];
    acc[product.type].push(product);
    return acc;
  }, {});
}, [products, searchQuery, stockFilter, typeFilter]);
const renderProductGrid = (productsArr) => {
return (
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
{productsArr.map((product) => {
const inStock = product.inStock !== undefined ? product.inStock : product.in_stock;
return (
<ProductCard
key={product.id}
product={{ ...product, outOfStock: !inStock }}
/>
);
})}
</div>
);
};
const FilterButton = ({ label, value }) => (
<button
  onClick={() => setStockFilter(value)}
  className={`px-6 py-2.5 text-sm font-semibold transition-all ${
    stockFilter === value
      ? 'bg-white text-slate-900 shadow-sm rounded-lg'
      : 'text-slate-500 hover:text-slate-700'
  }`}
>
  {label}
</button>

);
return (
<div className="min-h-screen bg-slate-50 p-8">
<ToastContainer position="top-right" autoClose={3000} />
  <div className="max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-500">
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Product Catalog</h1>
        <p className="text-slate-500 text-sm mt-1">Manage categories, items, and current stock availability.</p>
      </div>
      <div className="flex items-center gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="Search item name..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition-all"
          />
        </div>
        
        <div className="bg-slate-100 p-1.5 rounded-xl flex">
          <FilterButton label="All" value="all" />
          <FilterButton label="In Stock" value="inStock" />
          <FilterButton label="Out of Stock" value="outOfStock" />
        </div>
        
        <button 
          onClick={() => setIsFormVisible(true)}
          className="flex items-center gap-2 bg-blue-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-blue-100 hover:bg-blue-800 transition-all transform hover:scale-[1.02] active:scale-95"
        >
          <Plus size={20} strokeWidth={3} />
          Add Item
        </button>
      </div>
    </div>

    {loading ? (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    ) : Object.keys(filteredProducts).length === 0 ? (
      <div className="text-center py-20">
        <p className="text-slate-400 text-lg">No products found</p>
      </div>
    ) : (
      Object.entries(filteredProducts).map(([type, typeProducts]) => (
        <section key={type} className="space-y-6">
          <div className="flex items-baseline gap-3">
            <h2 className="text-xl font-bold text-slate-900">{type}</h2>
            <span className="text-sm font-medium text-slate-400">{typeProducts.length} items</span>
          </div>
          {renderProductGrid(typeProducts)}
        </section>
      ))
    )}
  </div>

  {isFormVisible && <ProductForm />}
</div>
);
};
export default Product;